/**
 * The bell's live line (المرحلة 1.2, F07).
 *
 * The server pushes a person's notifications to every screen they have open
 * (Backend/lib/liveSocket.js, `?notify=1`): a new one, which ones were read, and
 * which were removed. The token goes in the first frame, never in the URL, as
 * the control channel does it (hooks/useControlChannel.js).
 *
 * Whenever the line (re)connects, `onMessage` is called with
 * `{ type: 'notify-ready' }` so the screen re-reads its list and nothing sent
 * while it was away is missed; while it is down, a slow poll does the same.
 */

import { useEffect, useRef } from 'react';
import { API_BASE_URL, tokenStore } from '../api/client';

const BACKOFF_MS = [1000, 3000, 8000, 20000, 45000];
const POLL_MS = 2 * 60 * 1000;

function socketUrl() {
  const base = /^https?:/.test(API_BASE_URL)
    ? API_BASE_URL.replace(/^http/, 'ws')
    : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}${API_BASE_URL}`;
  return `${base}/live/ws?notify=1`;
}

/**
 * @param {boolean}  enabled    a signed-in session whose bell lives on the server
 * @param {Function} onMessage  called with each message the server pushes
 */
export function useNotifyChannel(enabled, onMessage) {
  const latest = useRef(onMessage);
  latest.current = onMessage;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof WebSocket === 'undefined') return undefined;
    let stopped = false;
    let ws = null;
    let retry = null;
    let failures = 0;
    let open = false;

    const connect = () => {
      if (stopped) return;
      try {
        ws = new WebSocket(socketUrl());
      } catch {
        schedule();
        return;
      }
      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({ auth: tokenStore.get() }));
        } catch {
          /* closing */
        }
      };
      ws.onmessage = (event) => {
        let message = null;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (!message || !message.type) return;
        if (message.type === 'notify-ready') {
          failures = 0;
          open = true;
        }
        latest.current(message);
      };
      ws.onclose = () => {
        ws = null;
        open = false;
        schedule();
      };
      ws.onerror = () => {
        try {
          ws && ws.close();
        } catch {
          /* already closed */
        }
      };
    };

    const schedule = () => {
      if (stopped) return;
      const wait = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)];
      failures += 1;
      retry = setTimeout(connect, wait);
    };

    connect();
    const poll = setInterval(() => {
      if (!open) latest.current({ type: 'poll' });
    }, POLL_MS);

    return () => {
      stopped = true;
      clearTimeout(retry);
      clearInterval(poll);
      if (ws) {
        ws.onclose = null;
        try {
          ws.close();
        } catch {
          /* already closed */
        }
      }
    };
  }, [enabled]);
}

export default useNotifyChannel;
